import { useEffect, useLayoutEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * Hero — pin-and-morph with generous dwell.
 *
 * Section height = 100vh + 1600px so the sticky pins for a full 1600px of scroll.
 *
 *   scrollYProgress (input mapped over the 1600px pin range):
 *     0     — RockLadder is huge and visually centered in the viewport.
 *     0.4   — Morph complete. RockLadder is small/purple/top-left. Tagline + subhead + CTAs fully visible.
 *     0.4 → 1.0 — DWELL. Every transform holds its final value. Nothing fades or moves. Hero stays static.
 *     >1.0  — Sticky releases and the section scrolls out naturally into the next block.
 *
 * All useTransform mappings use two stops (0 → 0.4) so Framer Motion clamps after 0.4 — the
 * final values are held exactly. No third stops. No whileInView. No exit variants.
 * Runtime-measured deltas ensure RockLadder is visually centered at scroll 0 on any viewport.
 */
export default function HeroWhite() {
  const reduced = useReducedMotion();
  const heroRef = useRef(null);
  const bigRef = useRef(null);      // natural (final-state) position of "RockLadder"

  // Motion values for the measured deltas. Using motion values (not React state) so
  // useTransform composition picks up updated values reactively.
  const dxMv = useMotionValue(0);
  const dyMv = useMotionValue(0);
  const readyMv = useMotionValue(0); // 0 until measured, then 1

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end end"],
  });

  // Measure the natural (final) position of the "RockLadder" element and compute
  // the pixel delta needed to visually center it in the viewport at scroll 0.
  useLayoutEffect(() => {
    const measure = () => {
      const el = bigRef.current;
      if (!el) return;
      // Temporarily neutralize the transform so we get the natural layout box.
      const prev = el.style.transform;
      el.style.transform = "none";
      const rect = el.getBoundingClientRect();
      el.style.transform = prev;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const naturalCenterX = rect.left + rect.width / 2;
      const naturalCenterY = rect.top + rect.height / 2;
      dxMv.set(vw / 2 - naturalCenterX);
      dyMv.set(vh / 2 - naturalCenterY);
      readyMv.set(1);
    };

    const t = setTimeout(measure, 60);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [dxMv, dyMv, readyMv]);

  // RockLadder morph — 0 → 0.4, then LOCKED. Delta scales down from measured value → 0.
  // Explicit 4-stop clamps ([0, morphStart, morphEnd, 1]) prevent Framer Motion v12's
  // WAAPI-backed linear() easing from extrapolating past the last stop.
  const bigScale       = useTransform(scrollYProgress, [0, 0, 0.4, 1], [1, 1, 0.16, 0.16]);
  const bigX = useTransform([scrollYProgress, dxMv], ([p, dx]) => {
    const t = Math.min(Math.max(p / 0.4, 0), 1);
    return dx * (1 - t);
  });
  const bigY = useTransform([scrollYProgress, dyMv], ([p, dy]) => {
    const t = Math.min(Math.max(p / 0.4, 0), 1);
    return dy * (1 - t);
  });
  const bigLetter      = useTransform(scrollYProgress, [0, 0, 0.4, 1], ["-0.03em", "-0.03em", "0.24em", "0.24em"]);
  const bigColor       = useTransform(scrollYProgress, [0, 0.25, 0.4, 1], ["#0B0B10", "#0B0B10", "#5B2A86", "#5B2A86"]);
  const bigOpacity     = useTransform(readyMv, [0, 1], [0, 1]);

  // Final-state content — fade IN once, then LOCKED at 1. Use 4 stops with explicit
  // held endpoints so WAAPI doesn't extrapolate past 0.4.
  const taglineOpacity = useTransform(scrollYProgress, [0, 0.18, 0.36, 1], [0, 0, 1, 1]);
  const taglineY       = useTransform(scrollYProgress, [0, 0.18, 0.36, 1], [28, 28, 0, 0]);
  const subheadOpacity = useTransform(scrollYProgress, [0, 0.24, 0.4, 1],  [0, 0, 1, 1]);
  const subheadY       = useTransform(scrollYProgress, [0, 0.24, 0.4, 1],  [16, 16, 0, 0]);
  const ctasOpacity    = useTransform(scrollYProgress, [0, 0.3, 0.4, 1],   [0, 0, 1, 1]);
  const ctasY          = useTransform(scrollYProgress, [0, 0.3, 0.4, 1],   [16, 16, 0, 0]);
  const hintOpacity    = useTransform(scrollYProgress, [0, 0.05, 1],       [1, 0, 0]);

  useEffect(() => {
    const t = setTimeout(() => { try { window.__RL_HERO_READY = true; } catch {} }, 40);
    const unsub = scrollYProgress.on("change", (v) => { try { window.__RL_SCROLL_P = v; } catch {} });
    return () => { clearTimeout(t); unsub(); };
  }, [scrollYProgress]);

  // Reduced motion — show final state statically, no animation, minimal height.
  if (reduced) {
    return (
      <section className="theme-editorial relative min-h-[92vh] flex items-center bg-white" data-testid="hero-white">
        <div className="mx-auto max-w-[1100px] w-full px-6 md:px-10 pt-28 pb-16 text-left">
          <div className="font-editorial-mono text-[10.5px] tracking-[0.24em] text-[#5B2A86] mb-4">ROCKLADDER</div>
          <h1 className="font-editorial-display text-[#0B0B10] leading-[0.98] tracking-[-0.03em]">
            <span style={{ fontSize: "clamp(48px, 8.6vw, 128px)" }}>
              Resilience for a<br />
              <span className="font-editorial-italic text-[#5B2A86]" style={{ fontStyle: "italic", fontWeight: 400 }}>
                Changing World.
              </span>
            </span>
          </h1>
          <p className="mt-8 max-w-[620px] text-[17px] leading-[1.55] text-[#4A4A55]">
            RockLadder provides end-to-end cybersecurity for organizations across India and global
            markets — helping build future-ready security programs that assess risk, strengthen
            defenses, and enable business growth.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-[#0B0B10] text-white px-6 py-3.5 text-[13.5px]">
              Book a Consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-[rgba(11,11,16,0.15)] text-[#0B0B10] px-6 py-3.5 text-[13.5px]">
              Explore Services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={heroRef}
      data-testid="hero-white"
      className="theme-editorial relative bg-white"
      style={{ height: "calc(100vh + 1600px)" }}
      aria-label="Hero"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Decorative ambient */}
        <div aria-hidden className="absolute inset-0 grid-editorial opacity-40" />
        <div aria-hidden className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(91,42,134,0.06),transparent_70%)]" />
        <div aria-hidden className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(245,184,65,0.08),transparent_70%)]" />

        {/* Left-aligned content column */}
        <div className="relative h-full mx-auto max-w-[1100px] px-6 md:px-10 flex flex-col justify-center">
          <div className="relative w-full">
            {/* Anchor: "RockLadder" — natural pos: top-left of the content column, above the tagline. */}
            <motion.div
              ref={bigRef}
              style={{
                scale: bigScale,
                x: bigX,
                y: bigY,
                letterSpacing: bigLetter,
                color: bigColor,
                transformOrigin: "left top",
                opacity: bigOpacity,
              }}
              className="inline-block will-change-transform pointer-events-none"
            >
              <div
                className="font-editorial-display leading-[0.86] whitespace-nowrap"
                style={{
                  fontSize: "clamp(84px, 15.5vw, 220px)",
                  fontVariationSettings: '"SOFT" 30, "opsz" 144',
                  fontWeight: 400,
                }}
              >
                RockLadder
              </div>
            </motion.div>

            {/* Spacer so tagline sits below the final small anchor */}
            <div aria-hidden className="h-4 md:h-6" />

            <motion.h1
              style={{ opacity: taglineOpacity, y: taglineY }}
              className="font-editorial-display leading-[0.98] tracking-[-0.03em] text-[#0B0B10] text-left"
            >
              <span style={{ fontSize: "clamp(48px, 8.6vw, 128px)", fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
                Resilience for a<br />
                <span
                  className="font-editorial-italic text-[#5B2A86]"
                  style={{ fontStyle: "italic", fontWeight: 400 }}
                >
                  Changing World.
                </span>
              </span>
            </motion.h1>

            <motion.p
              style={{ opacity: subheadOpacity, y: subheadY }}
              className="mt-8 md:mt-10 max-w-[620px] text-[16px] md:text-[18px] leading-[1.55] text-[#4A4A55] text-left"
            >
              RockLadder provides end-to-end cybersecurity for organizations across India and global
              markets — helping build future-ready security programs that assess risk, strengthen
              defenses, and enable business growth.
            </motion.p>

            <motion.div
              style={{ opacity: ctasOpacity, y: ctasY }}
              className="mt-10 md:mt-12 flex flex-wrap items-center gap-3 md:gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 rounded-full bg-[#0B0B10] text-white px-6 py-3.5 text-[13.5px] hover:bg-[#5B2A86] transition-colors"
              >
                Book a Consultation
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 rounded-full border border-[rgba(11,11,16,0.15)] text-[#0B0B10] px-6 py-3.5 text-[13.5px] hover:border-[#5B2A86] hover:text-[#5B2A86] transition-colors"
              >
                Explore Services
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div
          style={{ opacity: hintOpacity }}
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 bottom-8 md:bottom-10 flex flex-col items-center gap-2 font-editorial-mono text-[10px] tracking-[0.28em] text-[#5B2A86] z-30"
        >
          <span>SCROLL</span>
          <span className="block h-8 w-px bg-[#5B2A86] animate-pulse" />
        </motion.div>
      </div>
    </section>
  );
}
